/**
 * The tenant alert engine's codes about the edge probe — one GET of every live
 * app host per minute, over the public internet.
 *
 * Fingerprints: `VIBE_ALERT_EDGE_PROBE` is per tenant (ORGANIZATION scope,
 * entity `vibe_cluster`, entityId the organizationId), with the failing hosts on
 * the payload. The two fleet codes are PLATFORM scope, entity `vibe_fleet`,
 * entityId `fleet` — one row for the platform, because the fault they report is
 * shared by every tenant and owned by none.
 */
import type { IssueDefinition } from "../../issue-definition";
import { ISSUE_CATEGORY } from "../../issue-enums";

export const VIBE_ALERT_EDGE_ISSUES = {
  /**
   * Paging: this is the alert that reaches on-call before a user does — a
   * customer's app host failing, or slower than the SLO, from the internet.
   */
  VIBE_ALERT_EDGE_PROBE: {
    category: ISSUE_CATEGORY.OBSERVABILITY,
    message:
      "The edge probe has seen one of this tenant's app hosts FAILED, or answering slower than the latency SLO, for longer than the policy allows, through a colo customers use. The payload names each host, the verdict, the layer the failure was attributed to, the probe id and the Cloudflare ray id and colo.",
    severity: "critical",
    page: true,
    remedy:
      "Read the layer on the payload: `cloudflare` and `alb` are the edge in front of the tenant, `router` and `edge-authz` the tenant's ingress, `app` the app itself. Find the request in that layer's logs by the probe id or ray id on the payload. This resolves on its own once every host answers healthy."
  },

  /**
   * Paging: the prober not looking — or not looking at every live host — means no
   * tenant's edge is being watched, which is the loss the whole edge alert
   * exists to prevent.
   */
  VIBE_ALERT_EDGE_PROBE_FLEET: {
    category: ISSUE_CATEGORY.OBSERVABILITY,
    message:
      "The edge probe is not watching the fleet: its reading is absent, stale, or taken with its vantage control down, or its host census is incomplete — a live app host a user can reach was not probed. Every tenant's edge alert is held while the reading says nothing. The payload carries the reason and any unprobed apps.",
    severity: "high",
    page: true,
    remedy:
      "Read the reason on the payload. A stale or absent reading is the edge probe sweep not running — check its cron (the edge probe sweep) and the worker log. A vantage-down reading is the prober's own control host failing — check the backend's outbound path. An incomplete census names the apps it skipped; check why each has no probe target. This resolves on its own on the first complete, fresh reading."
  },

  /**
   * Not paging: a path fault seen from the probe's single vantage — usually a
   * colo no customer uses — is about where the probe stood, not about what
   * customers see.
   */
  VIBE_ALERT_EDGE_PROBE_COLO: {
    category: ISSUE_CATEGORY.OBSERVABILITY,
    message:
      "The edge probe saw failed or slow hosts through a Cloudflare colo, and the reading shows the colo — not any tenant — is the variable: a colo in the policy's non-customer list, or a vantage that landed on one. It also opens when nothing failed and the probe's vantage itself landed on a non-customer colo, which withholds colo-less drops from their tenants. No tenant was paged for it. The payload names the colo, and the hosts when any failed.",
    severity: "medium",
    remedy:
      "Read the colo on the payload. A non-customer colo (`edgeProbe.nonCustomerColos`) is an artifact of where the probe stood and needs no action beyond watching that it stays rare. A colo customers use, failing while another colo serves the same host, is a broken leg from that colo to the origin region — raise it with Cloudflare. This resolves on its own once the probe sees no colo-path finding."
  }
} as const satisfies Record<string, IssueDefinition>;
